import { Modal, ModalProps, Slide } from "@mui/material";
import { IconDefinition, faTimes } from "@outschool/icons";
import React from "react";

import { Theme } from "../../theme";
import Box, { BoxProps } from "../Box/Box";
import { Button, ButtonProps } from "../Button/Button";
import Icon from "../Icon/Icon";
import { IconButton } from "../IconButton/IconButton";
import { Typography } from "..";

export { Modal, ModalProps };

export interface ModalContentProps extends BoxProps {}
export interface ModalHeaderProps extends BoxProps {}

export const ModalContent = React.forwardRef(
  ({ sx, ...props }: BoxProps, ref: typeof Box) => (
    <Box
      ref={ref}
      sx={[
        (theme: Theme) => ({
          borderRadius: theme.spacing(24),
          backgroundColor: "white",
          overflowY: "auto",
          maxHeight: "95vh",
          width: "100%",
          padding: theme.spacing(24),
          maxWidth: "600px",
          margin: "auto",
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...props}
    />
  ),
);

export interface ModalCloseButtonProps
  extends Omit<ButtonProps, "aria-label" | "children"> {
  /**
   * Aria label for the close button. Required for accessibility.
   */
  "aria-label": string;
}
export const ModalCloseButton = React.forwardRef(
  (
    { sx, ...props }: ModalCloseButtonProps,
    ref: React.Ref<HTMLButtonElement>,
  ) => (
    <IconButton
      ref={ref}
      variant="text"
      sx={[
        (theme: Theme) => ({
          color: "neutral.900",
          width: theme.spacing(46),
          height: theme.spacing(46),
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      icon={faTimes}
      {...props}
    />
  ),
);

type BasicModalBaseProps = Omit<ModalProps, "children"> & {
  children?: React.ReactNode;
  modalContentProps?: Omit<ModalContentProps, "children">;
  modalCloseButtonProps?: Omit<ModalCloseButtonProps, "aria-label">;
};

type BasicModalWithCloseButtonProps = BasicModalBaseProps & {
  hasCloseButton: true;
  closeButtonArialLabel: string;
};

type BasicModalWithoutCloseButtonProps = BasicModalBaseProps & {
  hasCloseButton?: never;
  closeButtonArialLabel?: never;
};

export type BasicModalProps =
  | BasicModalWithCloseButtonProps
  | BasicModalWithoutCloseButtonProps;

/**
 * Basic modal composed of a `Modal`, `ModalContent`, and optional `ModalCloseButton`.
 *
 *
 * If `hasCloseButton` is true and `onClose` is provided, a close button will be rendered in the top right corner.
 *
 * @deprecated This component is deprecated. Please use OSModal instead.
 */
export const BasicModal = ({
  children,
  hasCloseButton,
  onClose,
  closeButtonArialLabel,
  modalContentProps,
  modalCloseButtonProps,
  ...props
}: BasicModalProps) => (
  <Modal onClose={onClose} {...props}>
    <ModalContent
      {...modalContentProps}
      sx={[
        {
          ...(hasCloseButton ? { position: "relative" } : {}),
        },
        ...(modalContentProps?.sx
          ? Array.isArray(modalContentProps?.sx)
            ? modalContentProps?.sx
            : [modalContentProps?.sx]
          : []),
      ]}
    >
      {hasCloseButton && onClose && (
        <ModalCloseButton
          aria-label={closeButtonArialLabel}
          onClick={() => {
            onClose();
            modalCloseButtonProps?.onClick?.();
          }}
          {...modalCloseButtonProps}
          sx={[
            {
              position: "absolute",
              top: 0,
              right: 0,
              zIndex: 10,
            },
            ...(modalCloseButtonProps?.sx
              ? Array.isArray(modalCloseButtonProps?.sx)
                ? modalCloseButtonProps?.sx
                : [modalCloseButtonProps?.sx]
              : []),
          ]}
        />
      )}
      {children}
    </ModalContent>
  </Modal>
);

export type ModalWithTitleProps = Omit<ModalProps, "children"> & {
  children?: React.ReactNode;
  headerTitle: React.ReactNode;
  headerIcon?: IconDefinition;
  modalHeaderProps?: Omit<ModalHeaderProps, "children">;
  modalContentProps?: Omit<ModalContentProps, "children">;
  modalCloseButtonProps?: Omit<ModalCloseButtonProps, "aria-label">;
  closeButtonArialLabel: string;
};

/**
 * Modal with a header containing a title, optional title icon, and a close button.
 *
 * @deprecated This component is deprecated. Please use OSModal instead.
 */
export const ModalWithTitle = ({
  headerTitle,
  headerIcon,
  children,
  onClose,
  closeButtonArialLabel,
  modalHeaderProps,
  modalContentProps,
  modalCloseButtonProps,
  ...props
}: ModalWithTitleProps) => (
  <Modal onClose={onClose} {...props}>
    <ModalContent
      {...modalContentProps}
      sx={[
        {
          overflowY: "hidden",
          padding: "0",
          boxShadow:
            "0px 0px 4px 0px rgba(0, 0, 0, 0.04), 0px 16px 56px 0px rgba(0, 0, 0, 0.16)",
        },
        ...(modalContentProps?.sx
          ? Array.isArray(modalContentProps?.sx)
            ? modalContentProps?.sx
            : [modalContentProps?.sx]
          : []),
      ]}
    >
      <Box
        flex
        sx={[
          (theme: Theme) => ({
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: theme.spacing(16),
            paddingBottom: theme.spacing(16),
            paddingLeft: theme.spacing(24),
            paddingRight: theme.spacing(24),
            borderBottom: theme.spacing(1),
            borderBottomStyle: "solid",
            borderColor: theme.palette.primary[50],
          }),
          ...(modalHeaderProps?.sx
            ? Array.isArray(modalHeaderProps?.sx)
              ? modalHeaderProps?.sx
              : [modalHeaderProps?.sx]
            : []),
        ]}
      >
        <Typography
          variant="subtitle1"
          sx={(theme: Theme) => ({
            color: theme.palette.primary.main,
            alignItems: "center",
          })}
        >
          {headerIcon && (
            <Icon
              icon={headerIcon}
              sx={(theme: Theme) => ({ marginRight: theme.spacing(8) })}
            />
          )}
          {headerTitle}
        </Typography>
        {onClose && (
          <ModalCloseButton
            aria-label={closeButtonArialLabel}
            onClick={() => {
              onClose();
              modalCloseButtonProps?.onClick?.();
            }}
            {...modalCloseButtonProps}
            sx={[
              (theme: Theme) => ({
                width: theme.spacing(34),
                height: theme.spacing(34),
              }),
              ...(modalCloseButtonProps?.sx
                ? Array.isArray(modalCloseButtonProps?.sx)
                  ? modalCloseButtonProps?.sx
                  : [modalCloseButtonProps?.sx]
                : []),
            ]}
          />
        )}
      </Box>
      {children}
    </ModalContent>
  </Modal>
);

// ------------------------------------------------------------
// OS Modal (Header, Content, Actions)
// ------------------------------------------------------------

export interface OSModalProps extends Pick<ModalProps, "open" | "onClose"> {
  /** Headline text displayed in the header. If omitted, the header still renders when the close button is visible. */
  title?: React.ReactNode;
  /** Primary action button element. Defaults to contained, primary if variant/color are not specified. */
  actionPrimary?: React.ReactElement<ButtonProps>;
  /** Secondary action button element. Defaults to text, primary if variant/color are not specified. */
  actionSecondary?: React.ReactElement<ButtonProps>;
  /** When true, hides the close button. Backdrop click still triggers onClose. @default false */
  hideCloseButton?: boolean;
  /** Horizontal alignment strategy for the actions area. @default 'center-full' */
  horizontalActionAlignment?: "left" | "right" | "center-full";
  /** Orientation to lay out two action buttons. @default 'horizontal' */
  actionOrientation?: "horizontal" | "vertical";
  /** Children to render within the scrollable content area. */
  children?: React.ReactNode;
  /** Sticky behavior for sections while scrolling ('none', 'header', 'actionFooter', or 'headerAndActionFooter'). @default 'headerAndActionFooter' */
  stickySection?: "none" | "actionFooter" | "header" | "headerAndActionFooter";
  /** Width of the modal in pixels on desktop. @default 640px **/
  modalDesktopWidth?: number;
}

const defaultModalDesktopWidth = 640;

export const OSModal = ({
  open,
  onClose,
  title,
  actionPrimary,
  actionSecondary,
  hideCloseButton = false,
  horizontalActionAlignment = "center-full",
  actionOrientation = "horizontal",
  children,
  stickySection = "headerAndActionFooter",
  modalDesktopWidth = defaultModalDesktopWidth,
}: OSModalProps) => {
  const hasStickyFooter =
    stickySection === "actionFooter" ||
    stickySection === "headerAndActionFooter";

  const hasStickyHeader =
    stickySection === "header" || stickySection === "headerAndActionFooter";

  const hasActionButtons = Boolean(actionPrimary) || Boolean(actionSecondary);

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Box
        sx={(theme: Theme) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          minHeight: "100%",
          pointerEvents: "none",
          [theme.breakpoints.down("sm")]: {
            alignItems: "flex-end", // Always slide from bottom on mobile
          },
        })}
      >
        <Slide direction="up" in={open} timeout={300}>
          <Box
            sx={[
              (theme: Theme) => ({
                display: "flex",
                flexDirection: "column",
                position: "relative",
                backgroundColor: theme.palette.common.white,
                borderRadius: theme.spacing(16),
                overflow: "hidden",
                pointerEvents: "auto",
                width: modalDesktopWidth,
                maxWidth: 640,
                minWidth: 320,
                maxHeight: "95vh",
                margin: "0 auto",
                boxShadow:
                  "0px 0px 4px 0px rgba(0, 0, 0, 0.04), 0px 16px 56px 0px rgba(0, 0, 0, 0.16)",
                [theme.breakpoints.down("sm")]: {
                  // Bottom sheet behavior on small screens - always full width with side gutters
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  // Remove bottom border radius on mobile for sliding effect
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                },
              }),
            ]}
          >
            {/* Scroll Area includes optional header, content, and action footer */}
            <Box
              sx={{
                overflowY: "auto",
                flex: 1,
                // Keep inner content clipped to rounded corners
                WebkitOverflowScrolling: "touch",
              }}
            >
              {/* Header (title and/or close button) */}
              {(title || (!hideCloseButton && onClose)) && (
                <Box
                  flex
                  sx={[
                    (theme: Theme) => ({
                      alignItems: "center",
                      padding: theme.spacing(8),
                      paddingLeft: theme.spacing(24),
                      backgroundColor: theme.palette.common.white,
                      ...(hasStickyHeader
                        ? { position: "sticky", top: 0, zIndex: 2 }
                        : {}),
                      [theme.breakpoints.down("sm")]: {
                        paddingLeft: theme.spacing(16),
                      },
                    }),
                  ]}
                >
                  <Box
                    flex
                    sx={{
                      alignItems: "center",
                      flex: 1,
                      alignSelf: "flex-start",
                    }}
                  >
                    {title && (
                      <Typography
                        variant="h3"
                        sx={(theme: Theme) => ({
                          paddingTop: theme.spacing(24),
                          color: theme.palette.neutral[900],
                        })}
                      >
                        {title}
                      </Typography>
                    )}
                  </Box>
                  {!hideCloseButton && onClose && (
                    <ModalCloseButton
                      aria-label="Close"
                      onClick={onClose}
                      sx={(theme: Theme) => ({
                        width: theme.spacing(12),
                        height: theme.spacing(12),
                        alignSelf: "start",
                      })}
                    />
                  )}
                </Box>
              )}

              {/* Content */}
              <Box
                sx={[
                  (theme: Theme) => ({
                    padding: theme.spacing(24),
                    paddingTop: theme.spacing(8),
                    [theme.breakpoints.down("sm")]: {
                      paddingLeft: theme.spacing(16),
                      paddingRight: theme.spacing(16),
                      paddingBottom: hasActionButtons
                        ? theme.spacing(24)
                        : theme.spacing(16),
                    },
                  }),
                ]}
              >
                {children}
              </Box>

              {/* Actions */}
              {hasActionButtons && (
                <ActionFooter
                  primary={actionPrimary}
                  secondary={actionSecondary}
                  horizontalAlignment={horizontalActionAlignment}
                  orientation={actionOrientation}
                  sticky={hasStickyFooter}
                />
              )}
            </Box>
          </Box>
        </Slide>
      </Box>
    </Modal>
  );
};

type ActionFooterProps = {
  primary?: React.ReactElement<ButtonProps>;
  secondary?: React.ReactElement<ButtonProps>;
  horizontalAlignment?: "left" | "right" | "center-full";
  orientation?: "horizontal" | "vertical";
  sticky?: boolean;
};

/**
 * Helper to normalize sx prop into an array format for merging
 */
const normalizeSxArray = (sx: any): any[] => {
  if (!sx) {
    return [];
  }
  return Array.isArray(sx) ? sx : [sx];
};

/**
 * Merges default props with existing button props, preserving existing props
 * when they exist and applying defaults when they don't.
 *
 * @param existingProps - The existing button props
 * @param defaults - Default props to apply (won't override existing props)
 * @returns Merged props object
 */
const mergeButtonProps = (
  existingProps: ButtonProps,
  defaults: Partial<ButtonProps> & { sx?: any; fullWidth?: boolean },
): ButtonProps => {
  return {
    ...existingProps,
    color: (existingProps.color as any) ?? (defaults.color as any),
    variant: (existingProps.variant as any) ?? (defaults.variant as any),
    fullWidth: existingProps.fullWidth ?? defaults.fullWidth ?? false,
    sx: [
      ...normalizeSxArray(defaults.sx),
      ...normalizeSxArray(existingProps.sx),
    ],
  };
};

const ActionFooter = ({
  primary,
  secondary,
  horizontalAlignment,
  orientation,
  sticky,
}: ActionFooterProps) => {
  const hasPrimary = Boolean(primary);
  const hasSecondary = Boolean(secondary);
  const hasTwoButtons = hasPrimary && hasSecondary;

  const containerSx = (theme: Theme) => ({
    borderTop: `${theme.spacing(1)} solid ${theme.palette.neutral[100]}`,
    padding: theme.spacing(24),
    backgroundColor: theme.palette.common.white,
    ...(sticky && {
      position: "sticky" as const,
      bottom: 0,
      zIndex: 2,
    }),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(16),
      paddingTop: theme.spacing(24),
      paddingBottom: theme.spacing(24),
    },
  });

  // Layout configuration
  const isVertical = orientation === "vertical";
  const isHorizontal = orientation === "horizontal";
  const isCenterFull = horizontalAlignment === "center-full";
  const isRightAligned = horizontalAlignment === "right";

  // Flexbox properties
  const flexDirection = isVertical ? "column" : "row";
  const justifyContent = (() => {
    if (isRightAligned) {
      return "flex-end";
    }
    if (isCenterFull) {
      return "center";
    }
    return "flex-start";
  })();

  const alignItems = (() => {
    if (isVertical) {
      if (isRightAligned) {
        return "flex-end";
      }
      if (isCenterFull) {
        return "stretch";
      }
      return "flex-start";
    }
    return "center";
  })();

  // Button configuration
  const shouldStretchButtons = isVertical || (isHorizontal && isCenterFull);

  const buttonSx = [
    // Vertical layout: stretch all buttons
    isVertical && { alignSelf: "stretch" },
    // Horizontal center-full: distribute space evenly
    isHorizontal && isCenterFull && { flex: 1, minWidth: 0 },
    // Mobile: stack buttons vertically when there are two
    (theme: Theme) =>
      hasTwoButtons && {
        [theme.breakpoints.down("sm")]: {
          alignSelf: "stretch",
          flex: "initial",
          minWidth: "auto",
        },
      },
  ];

  // Apply defaults to button props
  const primaryButtonProps =
    primary &&
    mergeButtonProps(primary.props, {
      color: "primary",
      variant: "contained",
      sx: buttonSx,
      fullWidth: shouldStretchButtons,
    });

  const secondaryButtonProps =
    secondary &&
    mergeButtonProps(secondary.props, {
      color: "primary",
      variant: "text",
      sx: buttonSx,
      fullWidth: shouldStretchButtons,
    });

  return (
    <Box sx={containerSx as any}>
      <Box
        flex
        sx={(theme: Theme) => ({
          flexDirection,
          flexWrap: isHorizontal ? "nowrap" : "wrap",
          alignItems,
          justifyContent,
          width: "100%",
          gap: theme.spacing(16),
          [theme.breakpoints.down("sm")]: hasTwoButtons && {
            flexDirection: "column",
            alignItems: "stretch",
          },
        })}
      >
        {secondaryButtonProps && <Button {...secondaryButtonProps} />}
        {primaryButtonProps && <Button {...primaryButtonProps} />}
      </Box>
    </Box>
  );
};
